import ColumnLayout from "@components/ColumnLayout";
import clsx from "clsx";
import EntryGrid from "components/EntryGrid";
import debounce from "lodash/debounce";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { Seomatic } from "react-seomatic";

import BlogLayoutNewsletterForm from "@components/BlogLayoutNewsletterForm";
import DotBorder from "@components/DotBorder";
import EntryCarouselBlock from "@components/EntryCarouselBlock";
import EntryFilter from "@components/EntryFilter";
import { ArrowLeft } from "@components/Icons";
import Message from "@components/Message";
import PageSections from "@components/PageSections";
import PageTitle from "@components/PageTitle";
import useSetHeaderTheme from "@hooks/useSetHeaderTheme";
import Layout from "@layouts/Layout";
import Link from "next/link";
import useMeasure from "react-use-measure";

const ResourcesLayout = (page) => {
	const {
		pageEntry,
		blogPageEntry,
		resources = {},
		category,
		resourceTypes,
		topics,
		industries,
		navigation,
		searchQuery,
		formData,
		posts
	} = page;
	useSetHeaderTheme("dark");

	const router = useRouter();

	const [imageHeight, setImageHeight] = useState(100);

	const handleImageHeight = debounce((newImageHeight) => {
		setImageHeight((oldHeight) =>
			newImageHeight > 0 ? newImageHeight : oldHeight
		);
	}, 500);

	// Handle fetching status
	const [isFetching, setIsFetching] = useState(false);

	const handleSearch = (value) => {
		setIsFetching(true);

		router.push(`/resources/search?q=${value}`, undefined, {
			scroll: false
		});
	};

	useEffect(() => {
		const handleRouteChange = () => {
			setIsFetching(false);
		};

		router.events.on("routeChangeComplete", handleRouteChange);

		return () => {
			router.events.off("routeChangeComplete", handleRouteChange);
		};
	}, [router]);

	// When layout is used for categories, make sure correct seomatic data gets loaded
	let seomatic = pageEntry.seomatic;
	if (typeof category !== "undefined") {
		seomatic = category.seomatic;
	}

	return (
		<Layout navigation={navigation}>
			<Seomatic Head={Head} {...seomatic} />
			<PageTitle summary={pageEntry.summary}>{pageEntry.title}</PageTitle>
			{!searchQuery && !category && (
				<div
					className={clsx(
						"ResourcesLayout__header",
						"mb-7.5 lg:mb-22 px-5 xl:px-0"
					)}
				>
					<ColumnLayout disableMobileYSpace>
						<ColumnLayout.MainColumn noXPadding>
							<EntryCarouselBlock
								isFlush={true}
								aspectRatio={"square"}
								thumbnailImageHeight={imageHeight}
								entries={pageEntry.featuredEntries}
								onImageHeight={handleImageHeight}
							/>
						</ColumnLayout.MainColumn>
						<ColumnLayout.SidebarColumn className="bg-yellow-01">
							<BlogLayoutNewsletterForm
								formHeading={blogPageEntry.formHeading}
								formDescription={blogPageEntry.formDescription}
								form={blogPageEntry?.form?.[0]}
								formData={formData}
								successMessage={blogPageEntry.successMessage}
							/>
						</ColumnLayout.SidebarColumn>
					</ColumnLayout>
				</div>
			)}

			<div className="SectionHeading__container relative max-w-screen-xl block mx-auto px-5 py-7.5 mb-15 bg-white-01 box-sizing">
				<h2 className="text-4xl text-center font-medium">
					{!!posts?.length
						? "Blog Posts"
						: !!category?.title
						? category.title
						: "All Resources"}
				</h2>
				{(!!posts?.length || !!category?.length) && (
					<a
						className="text-center mt-5 text-purple-01 font-medium flex w-ful justify-center items-center"
						href="/resources"
					>
						<ArrowLeft />
						<div className="ButtonText ml-3">View All Research</div>
					</a>
				)}

				<DotBorder />
			</div>

			<EntryFilter
				categoryGroups={[
					{
						handle: "resourceTypes",
						label: "Filter by type",
						categories: resourceTypes
					},
					{
						handle: "topics",
						label: "Filter by topic",
						categories: topics
					}
				]}
				selectedCategory={posts ? "blog" : category}
				isFetching={isFetching}
				handleSearch={handleSearch}
				initialValue={searchQuery}
			/>
			<div
				className={clsx(
					"EntryGrid__container pb-15 max-w-screen-xl mx-auto px-5 xl:px-0"
				)}
			>
				<EntryGrid
					entries={posts ? posts : resources}
					indentMobile
					thumbnailAspectRatio="square"
					showDate
				/>
				{resources.length === 0 && !!searchQuery && (
					<Message message="No resources match your search criteria. Please try again." />
				)}
			</div>
		</Layout>
	);
};

export default ResourcesLayout;
